import React, { useCallback, useEffect, useState } from "react";
import Modal from "@mui/material/Modal";
import Fade from "@mui/material/Fade";
import Backdrop from "@mui/material/Backdrop";
import { Box, TextField } from "@mui/material";
import {
  ButtonWrapper,
  CloseBtn,
  ModalDescription,
  ModalHeader,
  ModalTitle,
  StyedImg,
  StyledBox,
  StyledBoxImage,
  StyledBoxReview,
  StyledDeclineNoteField,
  StyledGrid,
} from "./style";
import CustomButton from "../../common/button/CustomButton";
import theme from "../../../theme";
import Grid from "@mui/material/Grid2";
import close from "../../../assets/images/x-close-black.png";
import { useDispatch } from "react-redux";
import { VIRTUAL_LOCKER_ITEM_STATUS_CHANGE } from "../../../actions/VirtualLockerAction";

export default function ReviewApprove({ status, open, handleClose, images, id, getItemLocker }) {
  const dispatch = useDispatch();
  const [previewIndex, setPreviewIndex] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [declineNote, setDeclineNote] = useState("");
  const isDecline = status === "rejected";

  useEffect(() => {
    if (!open) {
      setDeclineNote("");
    }
  }, [open, status]);

  const openPreview = (index) => {
    setPreviewIndex(index);
    setPreviewOpen(true);
  };

  const closePreview = () => {
    setPreviewOpen(false);
  };

  const itemStatusChange = useCallback(() => {
    const trimmedNote = declineNote.trim();

    dispatch({
      type: VIRTUAL_LOCKER_ITEM_STATUS_CHANGE,
      payload: {
        id,
        status,
        ...(isDecline && trimmedNote ? { customer_decline_note: trimmedNote } : {}),
        onSuccess: () => {
          getItemLocker();
          handleClose();
        },
      },
    });
  }, [dispatch, status, getItemLocker, handleClose, id, isDecline, declineNote]);

  return (
    <>
      <Modal open={open} onClose={handleClose}>
        <StyledBox>

          {/* ── Fixed header ── */}
          <ModalHeader>
            <ModalTitle>Review Uploaded Images</ModalTitle>
            <CloseBtn onClick={handleClose}>
              <img src={close} alt="close" loading="lazy" width={18} height={18} />
            </CloseBtn>
          </ModalHeader>

          {/* ── Description ── */}
          <ModalDescription>
            {isDecline ? (
              <>
                Review the uploaded pictures. If they do not match your expectations, you may
                add a note and click <strong>Decline</strong>.
              </>
            ) : (
              <>
                Compare the product images uploaded by the sender before proceeding.
                If everything looks good, click <strong>Approve</strong> to continue.
              </>
            )}
          </ModalDescription>

          {/* ── Scrollable image grid ── */}
          <StyledBoxReview>
            <Grid container columnSpacing={2} rowSpacing={2}>
              {images?.map((data, index) => (
                <StyledGrid
                  key={index}
                  size={{ lg: 4, md: 4, sm: 6, xs: 6 }}
                  onClick={() => openPreview(index)}
                >
                  <StyledBoxImage>
                    <StyedImg src={data} alt={`product-${index + 1}`} loading="lazy" />
                  </StyledBoxImage>
                </StyledGrid>
              ))}
            </Grid>
          </StyledBoxReview>

          {isDecline && (
            <StyledDeclineNoteField>
              <TextField
                fullWidth
                multiline
                minRows={3}
                maxRows={6}
                label="Decline note (optional)"
                placeholder="Tell us why you are declining these pictures..."
                value={declineNote}
                onChange={(event) => setDeclineNote(event.target.value)}
                inputProps={{ maxLength: 500 }}
                error={declineNote.length >= 500}
                helperText={
                  declineNote.length >= 500
                    ? "Maximum 500 characters reached."
                    : `${declineNote.length}/500`
                }
              />
            </StyledDeclineNoteField>
          )}

          {/* ── Fixed footer buttons ── */}
          <ButtonWrapper>
            <CustomButton
              padding="10px 32px"
              fontSize={theme.typography.small.fontSize}
              height="44px"
              btnLabel="Cancel"
              btntype="white"
              color={theme.palette.custom.lockerValueColor}
              fontWeight={theme.typography.fontWeight.medium}
              onClick={handleClose}
            />
            <CustomButton
              padding="10px 32px"
              fontSize={theme.typography.small.fontSize}
              height="44px"
              btnLabel={status === "approved" ? "Approve" : "Decline"}
              btntype={status === "approved" ? "primary" : "danger"}
              color={theme.palette.custom.white}
              fontWeight={theme.typography.fontWeight.medium}
              disabled={isDecline && declineNote.length >= 500}
              onClick={itemStatusChange}
            />
          </ButtonWrapper>
        </StyledBox>
      </Modal>

      {/* ── Full-screen image preview ── */}
      {/* ── Image preview modal ── */}
      <Modal
        open={previewOpen}
        onClose={closePreview}
        slots={{ backdrop: Backdrop }}
        slotProps={{ backdrop: { timeout: 300, style: { background: "rgba(10,20,50,0.9)" } } }}
        keepMounted={false}
      >
        <Fade in={previewOpen} timeout={250}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              outline: "none",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Box
              component="img"
              src={previewIndex !== null ? images[previewIndex] : ""}
              alt="preview"
              sx={{
                maxWidth: "88vw",
                maxHeight: "82vh",
                borderRadius: "12px",
                boxShadow: "0 8px 40px rgba(0,0,0,0.6)",
                display: "block",
                objectFit: "contain",
              }}
            />
            {/* <Box sx={{ color: "rgba(255,255,255,0.7)", fontSize: "13px", fontWeight: 500, background: "rgba(0,0,0,0.4)", padding: "3px 14px", borderRadius: "20px" }}>
              {previewIndex !== null ? `${previewIndex + 1} / ${images?.length}` : ""}
            </Box> */}
          </Box>
        </Fade>
      </Modal>
    </>
  );
}
